import {
  BuildingType, Plot, ROAD_ROW, SimState, Tile,
  PLOT_W, PLOT_H, SEED_TYPES, emptyCargo,
} from './types';

// ── Tiny PRNG so the procedural world is deterministic ──────────────
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function blankTile(): Tile {
  return { surf: 'grass', crop: null, bld: null };
}

/**
 * Procedurally generate the starter lot:
 *  - Road strip along the south edge (rows ROAD_ROW..H-1).
 *  - Path connecting the road into the lot (one tile wide, at center column).
 *  - Starter clearing — a small rectangle of grass right above the path
 *    where the player can immediately start farming.
 *  - Forest: trees + bushes + boulders scattered with density rising
 *    as we move north (away from the road).
 *  - Mailbox + truck depot placed at the road edge, near the path entry.
 */
export function generatePlot(seed = 12345): Plot {
  const rng = mulberry32(seed);
  const tiles: Tile[][] = [];
  for (let r = 0; r < PLOT_H; r++) {
    const row: Tile[] = [];
    for (let c = 0; c < PLOT_W; c++) {
      row.push(blankTile());
    }
    tiles.push(row);
  }

  // 1. Road strip
  for (let r = ROAD_ROW; r < PLOT_H; r++) {
    for (let c = 0; c < PLOT_W; c++) {
      tiles[r][c].surf = 'road';
    }
  }

  // 2. Path: a vertical line from the road up to the clearing.
  const pathCol = Math.floor(PLOT_W / 2);
  for (let r = ROAD_ROW - 4; r < ROAD_ROW; r++) {
    tiles[r][pathCol].surf = 'path';
    tiles[r][pathCol + 1].surf = 'path';
  }

  // 3. Starter clearing: 4×3 grass with no clutter, right above the path.
  const clearC0 = pathCol - 1;
  const clearC1 = pathCol + 2;
  const clearR0 = ROAD_ROW - 7;
  const clearR1 = ROAD_ROW - 4;
  for (let r = clearR0; r < clearR1; r++) {
    for (let c = clearC0; c <= clearC1; c++) {
      tiles[r][c].surf = 'grass';
      tiles[r][c].thing = null;
    }
  }

  // 4. Forest: rows 0..ROAD_ROW-5. Density falls toward the road.
  for (let r = 0; r < ROAD_ROW - 4; r++) {
    for (let c = 0; c < PLOT_W; c++) {
      const tile = tiles[r][c];
      // Skip the clearing footprint
      if (r >= clearR0 && r < clearR1 && c >= clearC0 && c <= clearC1) continue;
      // Skip the path column
      if ((c === pathCol || c === pathCol + 1) && r >= ROAD_ROW - 4) continue;
      // Density grows with distance from road (north = denser). Trees are
      // sparser now so the forest reads as a glade and leaves room for
      // mushroom-bearing stumps + fallen logs without overcrowding.
      const distNorm = (ROAD_ROW - 4 - r) / (ROAD_ROW - 4);
      const density = 0.18 + distNorm * 0.32;  // 0.18 near clearing, 0.50 far north
      const roll = rng();
      if (roll < density) {
        const kind = rng();
        if (kind < 0.42)      tile.thing = 'tree-oak';     // fewer trees
        else if (kind < 0.72) tile.thing = 'bush';         // more bushes (berries)
        else if (kind < 0.88) tile.thing = 'rock-boulder';
        else                  tile.thing = 'tall-grass';
      } else {
        // Sprinkle some cosmetic decor on otherwise-empty grass
        const cosmetic = rng();
        if (cosmetic < 0.04) tile.decor = 'flower-daisy';
        else if (cosmetic < 0.07) tile.decor = 'flower-poppy';
        else if (cosmetic < 0.09) tile.decor = 'rock-small';
      }
    }
  }

  // 5. Permanent fixtures: truck depot at the road edge. The mailbox
  // was removed — seed orders go through the truck panel now.
  const mailR = ROAD_ROW - 1;
  tiles[mailR][pathCol + 3].bld = 'truck-depot';
  tiles[mailR][pathCol + 3].surf = 'path';

  return { width: PLOT_W, height: PLOT_H, tiles };
}

export function makeInitialState(): SimState {
  const plot = generatePlot();
  return {
    version: 12,
    plot,
    res: {
      money: 60,
      energy: 500,
      maxEnergy: 500,
      basket: 0,
      corn: 0,
      carrot: 0,
      flour: 0,
      wood: 0,
      seeds: { wheat: 200, corn: 0, carrot: 0 },
    },
    prog: {
      xp: 0,
      level: 1,
      unlocked: { hands: true, field: true, plant: true, axe: true, pickaxe: true, water: true, scythe: true },
    },
    selectedSeed: 'wheat',
    selectedBuilding: null,
    tool: 'hands',
    placing: null,
    truck: {
      status: 'idle',
      cargo: emptyCargo(),
      platform: emptyCargo(),
      timer: 0,
      returnPayload: 0,
      nextArrival: 30,    // first truck arrives 30s into the game
    },
    mailbox: { pending: null, eta: 0, paid: 0 },
    time: 0,
    zoom: { active: false, c: 0, r: 0 },
    props: defaultPropLayout(),
    moving: null,
    plots: [],
    activePlotIdx: null,
    weeds: [],
    freeTilled: [],
    freeSeeds: [],
    freeWet: [],
  };
}

/** Default tile positions for the world props. Mirrors what Diorama uses
 *  if `state.props` is missing on an old save (Sim constructor backfills). */
export function defaultPropLayout() {
  const pathCol = Math.floor(PLOT_W / 2);    // 15
  const rackRow = ROAD_ROW - 2;              // 22
  return {
    storage:  { c: pathCol - 6, r: rackRow },   //  9, 22
    seedbag:  { c: pathCol - 3, r: rackRow },   // 12, 22
    toolrack: { c: pathCol - 1, r: rackRow },   // 14, 22
  };
}

void SEED_TYPES;  // kept exported via types for UI consumers
